import { apiFetch } from '../../utils/apiFetch';

/**
 * notifyMeSlice
 *
 * Handles the "Notify Me when back in stock" feature.
 *
 * State shape (keyed by `${productId}_${variantId}`):
 *   notifyMeStatus: { [key]: 'idle' | 'loading' | 'registered' | 'error' }
 */
export const createNotifyMeSlice = (set, get) => ({
  // Map of `{productId}_{variantId}` → status
  notifyMeStatus: {},

  /**
   * Check current notify-me status for a product/variant from the server.
   */
  fetchNotifyMeStatus: async ({ product_id, product_variant_id, pincode }) => {
    const key = `${product_id}_${product_variant_id}`;
    set((state) => ({
      notifyMeStatus: { ...state.notifyMeStatus, [key]: 'loading' },
    }));
    try {
      const params = new URLSearchParams({ product_id, product_variant_id });
      if (pincode) params.append('pincode', pincode);
      const data = await apiFetch(
        `/api/v1/customer/products/notify-me/status?${params.toString()}`
      );
      const isRegistered = data?.data?.is_registered ?? data?.is_registered ?? false;
      set((state) => ({
        notifyMeStatus: {
          ...state.notifyMeStatus,
          [key]: isRegistered ? 'registered' : 'idle',
        },
      }));
      return isRegistered;
    } catch {
      set((state) => ({
        notifyMeStatus: { ...state.notifyMeStatus, [key]: 'idle' },
      }));
      return false;
    }
  },

  /**
   * Register to be notified when the product/variant is back in stock.
   */
  registerNotifyMe: async ({ product_id, product_variant_id, pincode }) => {
    const key = `${product_id}_${product_variant_id}`;
    set((state) => ({
      notifyMeStatus: { ...state.notifyMeStatus, [key]: 'loading' },
    }));
    try {
      await apiFetch('/api/v1/customer/products/notify-me', {
        method: 'POST',
        body: { product_id, product_variant_id, pincode },
      });
      set((state) => ({
        notifyMeStatus: { ...state.notifyMeStatus, [key]: 'registered' },
      }));
      return { success: true };
    } catch (err) {
      set((state) => ({
        notifyMeStatus: { ...state.notifyMeStatus, [key]: 'error' },
      }));
      return { success: false, message: err.message };
    }
  },

  /**
   * Unsubscribe from notify-me alerts for a product/variant.
   */
  removeNotifyMe: async ({ product_id, product_variant_id }) => {
    const key = `${product_id}_${product_variant_id}`;
    set((state) => ({
      notifyMeStatus: { ...state.notifyMeStatus, [key]: 'loading' },
    }));
    try {
      await apiFetch('/api/v1/customer/products/notify-me', {
        method: 'DELETE',
        body: { product_id, product_variant_id },
      });
      set((state) => ({
        notifyMeStatus: { ...state.notifyMeStatus, [key]: 'idle' },
      }));
      return { success: true };
    } catch (err) {
      // Revert back to registered on error
      set((state) => ({
        notifyMeStatus: { ...state.notifyMeStatus, [key]: 'registered' },
      }));
      return { success: false, message: err.message };
    }
  },
});
